import { parseContentPath } from '@kcftech/prometheus-files-sdk';
import { IDirectoryEntryDto } from '@kcftech/prometheus-client';

export type OrganizationScope = {
    scopeType: 'corporation' | 'location';
    scopeId: string;
};

export function toRequiredScope(
    corporationId: string | undefined,
    locationId: string | undefined,
): OrganizationScope {
    if (!locationId && !corporationId) {
        throw new Error('Either locationId or corporationId must be provided');
    }
    return locationId
        ? { scopeType: 'location', scopeId: locationId }
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          { scopeType: 'corporation', scopeId: corporationId! };
}

type ScopedIdProps = {
    scopeType: 'corporation' | 'location';
    scopeId: string;
    fileId: string;
};

export function parseScopedId(scopedId: string): ScopedIdProps {
    const [scopeTypeChar, scopeId, fileId] = scopedId.split('.');
    return {
        scopeType: scopeTypeChar === 'l' ? 'location' : 'corporation',
        scopeId,
        fileId,
    };
}

export type DirentIdentityInfo = {
    scopeType: 'corporation' | 'location';
    scopeId: string;
} & OneOfOrBoth<
    {
        id: string;
    },
    {
        /** path after [scope] */
        path: string;
    }
>;

type OneOfOrBoth<T, U> =
    | (T & { [key in keyof U]?: never })
    | (U & { [key in keyof T]?: never })
    | (T & U);

export function parseDirentIdentityInfo({
    contentPath,
    id,
}: {
    contentPath: string;
    id: string;
}): DirentIdentityInfo {
    const contentPathData = parseContentPath(contentPath);
    return {
        scopeType: contentPathData.scopeType,
        scopeId: contentPathData.scopeId,
        path: contentPathData.filePath,
        id,
    };
}

type ToDirententIdentityInfoParams = Pick<
    IDirectoryEntryDto,
    'CorporationId' | 'LocationId'
> &
    OneOfOrBoth<
        Partial<Pick<IDirectoryEntryDto, 'Id'>>,
        Partial<Pick<IDirectoryEntryDto, 'Path'>>
    >;

export function toDirentIdentityInfo({
    CorporationId,
    LocationId,
    Id,
    Path,
}: ToDirententIdentityInfoParams): DirentIdentityInfo {
    if (!LocationId && !CorporationId) {
        throw new Error(
            'Either CorporationId or LocationId must be provided (global files currently unsupported)',
        );
    }

    const scopeType = LocationId ? 'location' : 'corporation';
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const scopeId = (LocationId ?? CorporationId)!;
    return {
        scopeType,
        scopeId,
        id: Id,
        path: Path,
    } as DirentIdentityInfo;
}

/**
 * @param path a valid ContentPath or Scoped Id
 * @returns an SD file library url path
 */
export function createFileLibraryDirentNavPath(
    dirent: DirentIdentityInfo,
    type: 'file' | 'folder',
) {
    if (!dirent.id) {
        const baseFileLibraryRoute = `/${dirent.scopeType}/${dirent.scopeId}/settings/fileLibrary`;
        if (dirent.path === '/') {
            return baseFileLibraryRoute;
        } else {
            return `${baseFileLibraryRoute}/${type === 'folder' ? 'd/' : 'f/'}?`;
        }
    }

    // assuming this is a valid Scoped File Id
    return `/${dirent.scopeType}/${dirent.scopeId}/settings/fileLibrary/${
        type === 'folder' ? 'd/' : 'f/'
    }${dirent.id}`;
}

export function fileNameFromPath(path: string): string {
    const idx = path.lastIndexOf('/');
    return path.substring(idx + 1);
}
